import { Injectable } from '@angular/core';
import { formatDate } from '@angular/common';
import { TranslateService } from '@ngx-translate/core';

import { ColumnToNotDisplay } from 'src/app/enums/ColumnsToNotDisplay';
import { TagFilterType } from 'src/app/shared/models/tag/tag-filter-type';
import { IconType } from 'src/app/tinea-components/icon/enum/icon-type';
import { BaseTableMapper } from 'src/app/tinea-components/table/mapper/BaseTableMapper';
import { TableModel } from 'src/app/tinea-components/table/models/input/table-model';
import { TableRows } from 'src/app/tinea-components/table/models/input/table-rows';
import { TableTd } from 'src/app/tinea-components/table/models/input/table-td';
import { TableTh } from 'src/app/tinea-components/table/models/input/table-th';
import { ValueCell } from 'src/app/tinea-components/table/models/input/value-cell';
import { GraphTagList } from 'src/swagger-gen/web-secured/models';


@Injectable({
  providedIn: 'root',
})
export class TableTagListMapper extends BaseTableMapper  {
  // -- configuration
  readonly CSS_CLASS_CONFIG = 'table-tags';

  // dataId
  readonly COLUMN_ID_NAME = TagFilterType.NAME;
  readonly COLUMN_ID_CONNECTION_NAME = TagFilterType.CONNECTION_NAME;
  readonly COLUMN_ID_UNIT = TagFilterType.UNIT;
  readonly COLUMN_ID_LAST_DATETIME = TagFilterType.LAST_DATETIME;
  readonly COLUMN_ID_LAST_VALUE_TAG = TagFilterType.LAST_VALUE_TAG;
  readonly COLUMN_ID_LOW_THRESHOLD = TagFilterType.LOW_THRESHOLD;
  readonly COLUMN_ID_HIGH_THRESHOLD = TagFilterType.HIGH_THRESHOLD;
  readonly COLUMN_ID_VALUE_TYPE = TagFilterType.VALUE_TYPE;
  readonly COLUMN_ID_ACTION = TagFilterType.ACTION;

  readonly DISPLAY_NO_DATA = '--';


  /**
   * Current application language
   */
   language: 'fr' | 'en' = 'fr';

   /**
    * Date format to display depending of language
    */
   dateFormat: string;

  table: TableModel;
  columnsToNotDisplay: ColumnToNotDisplay;

  constructor(private translate: TranslateService) {
    super();
    this.table = new TableModel();
  }

  init(TagsList: GraphTagList, columnsToNotDisplay: ColumnToNotDisplay): TableModel {
    this.columnsToNotDisplay = columnsToNotDisplay;
    this.table = new TableModel();
    // configuration
    this.initConfiguration();
    // header
    this.initHeader();
    // data
    this.initDataRow(TagsList);

    return this.table;
  }

  initConfiguration(): void {
    this.table.configuration.cssClass = this.CSS_CLASS_CONFIG;
    this.table.configuration.noDataRowMsg =
      this.translate.instant('error.no_result');
  }

  initHeader(): void {
    const thName = new TableTh();
    thName.columnId = this.COLUMN_ID_NAME;
    const thNameCell = new ValueCell();
    thNameCell.displayedValue = this.translate.instant(
      'graphs.tag.' + TagFilterType.NAME
    );
    thName.valueCells.push(thNameCell);

    const thLastDate = this.createSimpleTh(this.COLUMN_ID_LAST_DATETIME, this.translate.instant('graphs.tag.LAST_DATETIME'));
    const thLastValueTag = this.createSimpleTh(this.COLUMN_ID_LAST_VALUE_TAG, this.translate.instant('graphs.tag.LAST_VALUE_TAG'));

    const thConnectionName = new TableTh();
    thConnectionName.columnId = this.COLUMN_ID_CONNECTION_NAME;
    const thConnectionNameCell = new ValueCell();
    thConnectionNameCell.displayedValue = this.translate.instant(
      'graphs.tag.' + TagFilterType.CONNECTION_NAME
    );
    thConnectionName.valueCells.push(thConnectionNameCell);

    const thUnit = new TableTh();
    thUnit.columnId = this.COLUMN_ID_UNIT;
    const thUnitCell = new ValueCell();
    thUnitCell.displayedValue = this.translate.instant(
      'graphs.tag.' + TagFilterType.UNIT
    );
    thUnit.valueCells.push(thUnitCell);

    const thLowThreshold = new TableTh();
    thLowThreshold.columnId = this.COLUMN_ID_LOW_THRESHOLD;
    const thLowThresholdCell = new ValueCell();
    thLowThresholdCell.displayedValue = this.translate.instant(
      'graphs.tag.' + TagFilterType.LOW_THRESHOLD
    );
    thLowThreshold.valueCells.push(thLowThresholdCell);

    const thHighThreshold = new TableTh();
    thHighThreshold.columnId = this.COLUMN_ID_HIGH_THRESHOLD;
    const thHighThresholdCell = new ValueCell();
    thHighThresholdCell.displayedValue = this.translate.instant(
      'graphs.tag.' + TagFilterType.HIGH_THRESHOLD
    );
    thHighThreshold.valueCells.push(thHighThresholdCell);

    const thValueType = new TableTh();
    thValueType.columnId = this.COLUMN_ID_VALUE_TYPE;
    const thValueTypeCell = new ValueCell();
    thValueTypeCell.displayedValue = this.translate.instant(
      'graphs.tag.' + TagFilterType.VALUE_TYPE
    );
    thValueType.valueCells.push(thValueTypeCell);

    const thAction = new TableTh();
    thAction.columnId = this.COLUMN_ID_ACTION;
    const thActionCell = new ValueCell();
    thActionCell.displayedValue = this.translate.instant(
      'graphs.tag.' + TagFilterType.ACTION
    );
    thAction.valueCells.push(thActionCell);

    const thList = (this.columnsToNotDisplay == ColumnToNotDisplay.NotDisplay) ? [
      thName,
      thUnit,
      thLastDate,
      thLastValueTag,
      thValueType,
      thAction,
    ] :
    [
      thName,
      thLastDate,
      thLastValueTag,
      thUnit,
      thLowThreshold,
      thHighThreshold,
      thValueType,
      thAction,
    ];

    this.setHeaderCells(thList);
  }

  initDataRow(TagsList: GraphTagList): void {
    TagsList.list.forEach((tag) => {

      if (this.language === 'fr') {
        this.dateFormat = 'dd/MM/yyyy HH:mm:ss';
        tag.lastValues.lastValueDatetime = formatDate(tag.lastValues.lastValueDatetime, this.dateFormat, 'fr-FR');

      } else {
        this.dateFormat = 'MM/dd/yyyy HH:mm:ss';
        tag.lastValues.lastValueDatetime = formatDate(tag.lastValues.lastValueDatetime, this.dateFormat, 'en-EN');
      }

      const tableRows = new TableRows(tag);

      const name = new TableTd();
      const nameCell = new ValueCell();
      nameCell.displayedValue = tag.displayName || this.DISPLAY_NO_DATA;
      name.valueCells.push(nameCell);

      const connectionName = new TableTd();
      const connectionNameCell = new ValueCell();
      connectionNameCell.displayedValue =
        tag.connectionName || this.DISPLAY_NO_DATA;
      connectionName.valueCells.push(connectionNameCell);

      const unit = new TableTd();
      const unitCell = new ValueCell();
      unitCell.displayedValue = tag.unit || this.DISPLAY_NO_DATA;
      unit.valueCells.push(unitCell);


      const lastDatetime = this.createSimpleTd(this.COLUMN_ID_LAST_DATETIME, tag.lastValues.lastValueDatetime || this.DISPLAY_NO_DATA);
      const lastValueTag = this.createSimpleTd(this.COLUMN_ID_LAST_VALUE_TAG, tag.lastValues.lastValue || this.DISPLAY_NO_DATA);

      const lowThreshold = new TableTd();
      const lowThresholdCell = new ValueCell();
      lowThresholdCell.displayedValue =
        tag.lowThreshold || this.DISPLAY_NO_DATA;
      lowThreshold.valueCells.push(lowThresholdCell);

      const highThreshold = new TableTd();
      const highThresholdCell = new ValueCell();
      highThresholdCell.displayedValue =
        tag.highThreshold || this.DISPLAY_NO_DATA;
      highThreshold.valueCells.push(highThresholdCell);

      const valueType = new TableTd();
      const valueTypeCell = new ValueCell();
      valueTypeCell.displayedValue = tag.valueType
        ? this.translate.instant('graphs.tag.value_type.' + tag.valueType)
        : this.DISPLAY_NO_DATA;
      valueType.valueCells.push(valueTypeCell);


      const action = new TableTd();
      const actionCell = new ValueCell();
      actionCell.dataId = !tag.unit ? '' : tag.unit;
      actionCell.cssClass = tag.uid;
      actionCell.displayedValue = '';
      actionCell.addIcon(
        'BEFORE',
        IconType.FONTAWESOME,
        'far fa-square unchecked-icon'
      );
      actionCell.addIcon(
        'AFTER',
        IconType.FONTAWESOME,
        'fas fa-check-square checked-icon'
      );
      action.valueCells.push(actionCell);

      // add cells
      tableRows.cells = (this.columnsToNotDisplay == ColumnToNotDisplay.NotDisplay) ? [
        name,
        // connectionName,
        unit,
        lastDatetime,
        lastValueTag,
        valueType,
        action,
      ] :
      [
        name,
        // connectionName,
        unit,
        lastDatetime,
        lastValueTag,
        lowThreshold,
        highThreshold,
        valueType,
        action,
      ];

      // push row
      this.table.dataRow.push(tableRows);
    });
  }

  setHeaderCells(thList: TableTh[]): void {
    this.table.header.cells = thList;
  }
}
